import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { CollectionGridMediaAssetWithEpisodes } from 'types';

import {
  CollectionAsset,
  CollectionProgramAsset,
  CollectionSeriesAsset,
  getProgramAiringDate,
  isCollectionProgramAsset,
} from '@kaltura-ott/tvpil-shared';

import { useCardRef, useMouseEnterHandle, useMouseLeaveHandle } from 'components/presenters/Card/hooks';
import { useRailContext } from 'components/widgets/Rail/components/RailProvider/RailProvider';
import { WidgetProviderSettings } from 'context/widgetProvider/types';
import { useWidgetContext } from 'context/widgetProvider/widgetProvider';
import { useParentalPinStore, useRecordRepository } from 'hooks';
import { getAriaLabelForAsset, getSeasonEpisodeTitleV2 } from 'utils';

import { useAiringDate } from '../../../../hooks/useAiringDate/useAiringDate';
import { useCollectionCardChannelInfo } from '../useCollectionCardChannelInfo/useCollectionCardChannelInfo';
import { useCollectionCardClickHandler } from '../useCollectionCardClickHandler/useCollectionCardClickHandler';
import { useCollectionCardHandleClickInfo } from '../useCollectionCardHandleClickInfo/useCollectionCardHandleClickInfo';
import { useCollectionCardImages } from '../useCollectionCardImages/useCollectionCardImages';
import { useCollectionCardLink } from '../useCollectionCardLink/useCollectionCardLink';
import { useCollectionCardMainProps } from '../useCollectionCardMainProps/useCollectionCardMainProps';
import { useCollectionCardRating } from '../useCollectionCardRating/useCollectionCardRating';

interface Props {
  item: CollectionAsset;
  itemWidth: number;
  imageHeight: number;
  hasNoDirectPlayback?: boolean;
}
// TODO: need to cover by units
function useCollectionCard({ item, itemWidth, imageHeight, hasNoDirectPlayback = false }: Props) {
  const intl = useIntl();

  // using version v2 of the rail provider
  const { onHover, nextButtons } = useRailContext();
  const {
    settings: { shouldShowRating, shouldShowEntitlementIndicator, imageType },
    type,
    isGrid,
  } = useWidgetContext<CollectionAsset, WidgetProviderSettings>();
  const {
    isTypeOfSeries,
    isFuture,
    isPurchasable,
    isLinkAsAsset,
    isCatchupSeries,
    isChannelAsset,
    isSingleVod,
    isAssetPlayOnExternalApp,
    isProgramAsset,
    contentMarker,
    isCollectionEpisode,
    isCollectionInAppAdultLink,
    adult,
    isVod,
    isProgramLive,
  } = useCollectionCardMainProps({
    data: item,
    isGrouped: false,
  });
  // getting airingDate
  const airingDate = getProgramAiringDate(item as CollectionProgramAsset);
  const airingObject = useAiringDate({ date: airingDate});
  // 
  const isAdult = adult || false;
  const { providerName } = item as CollectionProgramAsset;
  const isExternalProviderAsset = Boolean(providerName) && isAssetPlayOnExternalApp;
  const isAsPlayAsset = !isTypeOfSeries && !isFuture && !isPurchasable && !isLinkAsAsset;
  const isFutureProgram = isFuture && !isCatchupSeries;
  // checking for info button presentation
  const channelHasProgram = !isChannelAsset && !isLinkAsAsset;
  // restricted logic
  const parentalPinStore = useParentalPinStore();
  const isAdultPinNotEntered = !parentalPinStore.isAdultPinEntered;
  const isAdultBlocked = isAdultPinNotEntered && isAdult;
  const isRestricted = !!(
    (isAdult || isCollectionInAppAdultLink) &&
    isAdultPinNotEntered &&
    !isExternalProviderAsset
  );
  //
  const { link } = useCollectionCardLink({
    data: item,
    isSingleVod,
    isFutureProgram,
    collectionType: type,
    isLinkAsAsset,
    isAsPlayAsset,
    isChannelAsset,
    isProgramLive,
    hasNoDirectPlayback,
  });
  // refs
  const handleLinkRef = useRef(null);
  const { ref, handleRef } = useCardRef(itemWidth, imageHeight);
  // handlers
  const onMouseEnter = useMouseEnterHandle({ ref, itemWidth, imageHeight, isGrid, onHover, nextButtons });
  const onMouseLeave = useMouseLeaveHandle(onHover);
  const clickHandler = useCollectionCardClickHandler({
    data: item,
    link,
    mediaTypeParam: item.type,
    isExternalProviderAsset,
    isLinkAsAsset,
    isPurchasable,
    collectionType: type,
    isProgramAsset,
    isTypeOfSeries,
    isCatchupSeries,
    isCollectionEpisode,
    isCollectionInAppAdultLink,
    imageHeight,
    isAdultBlocked,
    isRestricted,
    parentalPinStore,
    hasNoDirectPlayback,
  });
  const { handleClickInfo } = useCollectionCardHandleClickInfo({
    id: item?.id!,
    externalId: undefined,
    mediaTypeParam: item.type,
    channelHasProgram,
    imageHeight,
    isLinkAsAsset,
  });
  // rating
  const { isDisplayRating, assetRating } = useCollectionCardRating({ shouldShowRating, item });
  // images urls
  const { backgroundImageUrl, defaultImageUrl } = useCollectionCardImages({
    item,
    imageType,
    imageWidth: itemWidth,
    imageHeight,
  });
  // title and channelName
  const { title, channelName } = useCollectionCardChannelInfo(item);
  // collectionProgramAsset type checking for AirTime presentation
  const collectionProgramAsset = isCollectionProgramAsset(item) ? item : undefined;
  // checking for play button presentation
  const isDisplayCartIcon = (isPurchasable && !isTypeOfSeries && shouldShowEntitlementIndicator) || false;
  // season / episode title definition
  let seasonEpisodeTitle = '';

  const isEpisodeInfo = (isProgramAsset || isCollectionEpisode) && !isCatchupSeries;
  if (isEpisodeInfo) {
    seasonEpisodeTitle = getSeasonEpisodeTitleV2(intl, item as CollectionGridMediaAssetWithEpisodes, ':', true);
  }

  // seriesId definition
  const seriesId = (item as CollectionSeriesAsset)?.seriesId!;

  // For record indication
  const recordRepository = useRecordRepository();
  const record = item?.id ? recordRepository.records.get(item.id.toString()) : null;
  const seriesRecord = seriesId ? recordRepository.seriesRecords.get(seriesId) : null;
  const isRecord = Boolean(record || seriesRecord);
  const isSeriesRecord = Boolean(seriesRecord);
  //

  // content marker
  const isDisplayContentMarker = isVod && !!contentMarker && !providerName;
  // getting aria-label text
  const ariaLabel = getAriaLabelForAsset({
    intl,
    title,
    channelName,
    description: seasonEpisodeTitle,
    isRestricted,
    isPurchaseContent: isDisplayCartIcon,
    airingObject,
    isRecord,
    isSeriesRecord,
    shouldPronouncedAiringTime: isFutureProgram
  });

  return {
    link,
    title,
    isAdult,
    isRecord,
    handleRef,
    ariaLabel,
    assetRating,
    channelName,
    airingObject,
    clickHandler,
    onMouseEnter,
    onMouseLeave,
    contentMarker,
    isLinkAsAsset,
    handleLinkRef,
    isAsPlayAsset: !hasNoDirectPlayback && isAsPlayAsset,
    isSeriesRecord,
    defaultImageUrl,
    isFutureProgram,
    isDisplayRating,
    handleClickInfo,
    channelHasProgram,
    isDisplayCartIcon,
    backgroundImageUrl,
    seasonEpisodeTitle,
    collectionProgramAsset,
    isDisplayContentMarker,
    isExternalProviderAsset,
  };
}

export { useCollectionCard };
